import React, { useState } from 'react';
import {
  Box,
  Button,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';

import {
  ISituacaoDoFechamento,
  motivoDaRecusa,
  useFecharCompetencia,
  useReabrirCompetencia,
} from '../../../../../hooks/useFechamentoContabilMensal';
import { COR, mesLongo } from '../regras';
import { oQueEstaAberto } from '../regrasDoFechamento';
import { Aviso } from './comuns';

export type AcaoDoFechamento = 'fechar' | 'reabrir';

/**
 * Confirmação de fechar ou reabrir: o que ainda está em aberto e o texto da pessoa
 * (justificativa opcional, motivo obrigatório). Fechada, a apuração deixa de ser
 * substituída pelo processamento. Sem arquivo para o Domínio (decisão de 03/11/2026).
 * Serve à barra da tela da empresa e ao detalhe da carteira.
 */
export function ConfirmacaoDoFechamento({
  acao,
  onSair,
  situacao,
  empresaId,
  competencia,
}: {
  acao: AcaoDoFechamento | null;
  onSair: () => void;
  situacao: ISituacaoDoFechamento;
  empresaId: number;
  competencia: string;
}) {
  const toast = useToast();
  const [texto, setTexto] = useState('');
  const fechar = useFecharCompetencia(empresaId, competencia);
  const reabrir = useReabrirCompetencia(empresaId, competencia);
  const fechada = situacao.situacao === 'FECHADO';
  const pelaMaquina = fechada && situacao.fechamento?.origem === 'sistema';
  const aberto = oQueEstaAberto(situacao);
  const sistema = situacao.sistema_proprio ?? null;
  const enviando = fechar.isPending || reabrir.isPending;

  const sair = () => {
    setTexto('');
    onSair();
  };
  const aoTerminar = (titulo: string) => ({
    onSuccess: () => {
      toast({ title: titulo, status: 'success', duration: 4000 });
      sair();
    },
    onError: (erro: unknown) =>
      toast({ title: motivoDaRecusa(erro), status: 'error', duration: 7000, isClosable: true }),
  });
  const confirmar = () => {
    if (acao === 'fechar') {
      fechar.mutate(
        { justificativa: texto.trim() || undefined },
        aoTerminar('Competência fechada'),
      );
    } else {
      reabrir.mutate({ motivo: texto.trim() }, aoTerminar('Competência reaberta'));
    }
  };

  return (
    <Modal isOpen={acao != null} onClose={sair} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">
          {acao === 'fechar' ? 'Fechar' : 'Reabrir'} {mesLongo(competencia)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {acao === 'fechar' ? (
            <>
              {aberto.length > 0 ? (
                <Aviso>
                  <b>Ainda em aberto nesta competência:</b>
                  <UnorderedList mt={1} ml={5}>
                    {aberto.map(item => (
                      <ListItem key={item}>{item}</ListItem>
                    ))}
                  </UnorderedList>
                </Aviso>
              ) : (
                <Aviso cor={COR.ok}>Nada em aberto nesta competência.</Aviso>
              )}
              {sistema && (
                <Text fontSize="12.5px" color={COR.apagado} mt={3}>
                  Fechamento em sistema próprio ({sistema}): aqui é só controle.
                </Text>
              )}
              <Text fontSize="12.5px" color={COR.apagado} mt={2} mb={1}>
                Depois de fechada, a revisão não é mais atualizada. Justificativa (opcional):
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="12.5px" color={COR.apagado} mb={1}>
                Reaberta, a competência volta a ser atualizada pelo processamento: se os dados
                mudaram, entra uma versão nova.
                {pelaMaquina &&
                  ' Reaberta pela área, ela não é mais fechada sozinha pelo sistema.'}{' '}
                Motivo (obrigatório):
              </Text>
              {situacao.reabrem_junto.length > 0 && (
                <Box mb={2}>
                  <Aviso>
                    Também reabrem, pelo sistema:{' '}
                    {situacao.reabrem_junto.map(c => mesLongo(c)).join(', ')} (fechadas sem
                    movimento). O sistema volta a fechá-las, em ordem, quando esta competência for
                    fechada de novo.
                  </Aviso>
                </Box>
              )}
            </>
          )}
          <Textarea
            size="sm"
            maxLength={300}
            value={texto}
            onChange={ev => setTexto(ev.target.value)}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={sair}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={enviando}
            isDisabled={acao === 'reabrir' && texto.trim().length < 3}
            onClick={confirmar}
          >
            {acao === 'fechar' ? 'Fechar competência' : 'Reabrir competência'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
